// ============================================================================
// Toast — minimal in-app notification banner. Replaces window.alert across
// the TCS app.
//
// API (consumer side):
//
//   import { useToast } from '@/shared/components/Toast';
//
//   const { success, error, info } = useToast();
//   success('Created SOP-0087', { description: 'Rose Medical' });
//
// Render once at app root:
//
//   <ToastProvider>
//     <App />
//   </ToastProvider>
//
// Behavior:
//   * Non-blocking (does not freeze the UI like window.alert).
//   * Up to 4 toasts stack bottom-center on mobile, top-right on >=sm.
//   * Auto-dismisses after 4s (configurable per call).
//   * aria-live=polite so screen readers announce, but don't interrupt.
//   * Variants: success (green), error (red), info (slate).
//
// Why hand-rolled vs sonner: keeps the dep count flat and the visual style
// in sync with the rest of the TCS design system tokens. Roughly 100 lines.
// ============================================================================

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { CheckCircle2, Info, XCircle, X } from 'lucide-react';

export type ToastVariant = 'success' | 'error' | 'info';

export interface ToastOptions {
  description?: string;
  /** ms before auto-dismiss. Pass 0 to disable auto-dismiss. */
  duration?: number;
}

interface ToastItem extends ToastOptions {
  id: number;
  variant: ToastVariant;
  title: string;
}

interface ToastApi {
  success: (title: string, opts?: ToastOptions) => number;
  error: (title: string, opts?: ToastOptions) => number;
  info: (title: string, opts?: ToastOptions) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Soft-fail: if a component renders outside the provider (e.g. tests),
    // log instead of throwing so we don't crash production over a toast.
    return {
      success: (t) => {
        console.info('[toast/success]', t);
        return 0;
      },
      error: (t) => {
        console.error('[toast/error]', t);
        return 0;
      },
      info: (t) => {
        console.info('[toast/info]', t);
        return 0;
      },
      dismiss: () => {},
    };
  }
  return ctx;
}

interface ProviderProps {
  children: ReactNode;
  /** Max toasts visible at once. Older ones are evicted FIFO. */
  max?: number;
}

export function ToastProvider({ children, max = 4 }: ProviderProps) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    setItems((cur) => cur.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (variant: ToastVariant, title: string, opts?: ToastOptions): number => {
      idRef.current += 1;
      const id = idRef.current;
      const item: ToastItem = {
        id,
        variant,
        title,
        description: opts?.description,
        duration: opts?.duration ?? 4000,
      };
      setItems((cur) => {
        const next = [...cur, item];
        if (next.length > max) next.splice(0, next.length - max);
        return next;
      });
      return id;
    },
    [max],
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (t, o) => push('success', t, o),
      error: (t, o) => push('error', t, o),
      info: (t, o) => push('info', t, o),
      dismiss,
    }),
    [push, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport items={items} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

// ----------------------------------------------------------------------------
// Viewport — fixed-position stack at top-right on desktop, bottom-center on
// mobile. Each item self-dismisses after its duration.
// ----------------------------------------------------------------------------

function ToastViewport({
  items,
  onDismiss,
}: {
  items: ToastItem[];
  onDismiss: (id: number) => void;
}) {
  return (
    <div
      role="region"
      aria-label="Notifications"
      className="pointer-events-none fixed inset-x-0 bottom-4 z-[100] flex flex-col items-center gap-2 px-3 sm:bottom-auto sm:left-auto sm:right-4 sm:top-4 sm:items-end sm:px-0"
    >
      {items.map((t) => (
        <ToastCard key={t.id} item={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastCard({
  item,
  onDismiss,
}: {
  item: ToastItem;
  onDismiss: (id: number) => void;
}) {
  // Auto-dismiss with the configured duration. 0 disables.
  useEffect(() => {
    if (!item.duration || item.duration <= 0) return;
    const handle = window.setTimeout(() => onDismiss(item.id), item.duration);
    return () => window.clearTimeout(handle);
  }, [item.id, item.duration, onDismiss]);

  const variantClasses: Record<ToastVariant, string> = {
    success: 'border-rag-green/40 bg-st-ok-bg text-st-ok-fg-strong',
    error: 'border-rag-red/40 bg-st-danger-bg text-rag-red',
    info: 'border-tcs-border bg-tcs-raised text-tcs-text',
  };
  const Icon =
    item.variant === 'success'
      ? CheckCircle2
      : item.variant === 'error'
        ? XCircle
        : Info;

  return (
    <div
      role={item.variant === 'error' ? 'alert' : 'status'}
      aria-live={item.variant === 'error' ? 'assertive' : 'polite'}
      className={`pointer-events-auto flex w-full max-w-sm items-start gap-2 rounded-lg border px-3 py-2 text-sm shadow-md transition ${variantClasses[item.variant]}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="font-medium leading-tight">{item.title}</div>
        {item.description && (
          <div className="mt-0.5 text-xs opacity-80">{item.description}</div>
        )}
      </div>
      <button
        type="button"
        onClick={() => onDismiss(item.id)}
        aria-label="Dismiss notification"
        className="shrink-0 rounded p-0.5 opacity-60 hover:opacity-100"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
